import React, { useContext, useState, useEffect } from "react";
import SignatureCanvas from "../../components/common/SignatureCanvas";
import Modal from "../../components/common/Modal";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { ThemeContext } from "../../hooks/ThemeContext";
import { UserContext } from "../../hooks/UserContext";
import SectionTitle from "../../components/common/SectionTitle";
import Card from "../../components/common/Card";
import ModalAdmin from "../../components/admin/ModalAdmin";
import Messages from "../../components/common/Messages";
import { Users, ShoppingBag, DollarSign, Mail, TrendingUp } from "lucide-react";
import { api } from "../../services/baseUrl";

// Pour la modale CGV
const ClientSpace = () => {
  const [showCgvModal, setShowCgvModal] = useState(false);
  // Toujours initialiser les contextes d'abord !
  const { theme } = useContext(ThemeContext);
  const { user, refreshUser } = useContext(UserContext);
  const location = useLocation();
  const isDark = theme === "dark";

  // Pour les earnings créateurs (hook manquant pour setCreatorsEarnings)
  const [creatorsEarnings, setCreatorsEarnings] = useState([]);
  // Pour les notifications email
  const [emailNotice, setEmailNotice] = useState("");
  // Pour le panneau admin (sera calculé dynamiquement plus bas)
  let adminPanel = null;
  // Pour les tabs (onglets)
  const tabs =
    user?.id_role === 3
      ? [
          { key: "settings", label: "Paramètres", icon: "⚙️" },
          { key: "orders", label: "Commandes", icon: "🛒" },
        ]
      : [
          { key: "dashboard", label: "Tableau de bord", icon: "🏠" },
          { key: "orders", label: "Commandes", icon: "🛒" },
          { key: "projects", label: "Projets", icon: "📁" },
          { key: "messages", label: "Messages", icon: "✉️" },
          { key: "quotes", label: "Devis", icon: "📄" },
          { key: "invoices", label: "Factures", icon: "🧾" },
          { key: "deliveries", label: "Livraisons", icon: "📦" },
          { key: "settings", label: "Paramètres", icon: "⚙️" },
        ];
  // Pour la coloration des statuts
  const statusColors = {
    Livré: isDark
      ? "bg-green-500/20 text-green-400"
      : "bg-green-100 text-green-700",
    "En attente": isDark
      ? "bg-yellow-500/20 text-yellow-400"
      : "bg-yellow-100 text-yellow-700",
    Terminé: isDark
      ? "bg-green-500/20 text-green-400"
      : "bg-green-100 text-green-700",
    Annulé: isDark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700",
    pending: isDark
      ? "bg-yellow-500/20 text-yellow-400"
      : "bg-yellow-100 text-yellow-700",
    paid: isDark
      ? "bg-green-500/20 text-green-400"
      : "bg-green-100 text-green-700",
    cancelled: isDark
      ? "bg-red-500/20 text-red-400"
      : "bg-red-100 text-red-700",
  };
  // Pour le profil admin (onglet stats/profil)
  const [adminProfileTab, setAdminProfileTab] = useState("stats");
  // Utilitaire pour les tendances
  const formatTrend = (value) => {
    if (value === undefined || value === null) return null;
    const sign = value > 0 ? "+" : value < 0 ? "-" : "";
    return `${sign}${Math.abs(value).toFixed(1)}%`;
  };
  // Utilitaire pour parser les dates françaises
  const parseFrenchDateTime = (value) => {
    if (!value) return 0;
    if (value instanceof Date) return value.getTime();
    if (typeof value === "string") {
      const match = value.match(
        /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})[ T]([0-9]{2}):([0-9]{2})/,
      );
      if (match) {
        const [, day, month, year, hours, minutes] = match;
        return new Date(
          `${year}-${month}-${day}T${hours}:${minutes}:00`,
        ).getTime();
      }
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };
  // Hooks d'état manquants pour éviter les erreurs de variables non définies
  const [profileForm, setProfileForm] = useState({
    email: "",
    firstname: "",
    lastname: "",
    nickname: "",
  });
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [purchasedFiles, setPurchasedFiles] = useState([]);
  const [deliveryFiles, setDeliveryFiles] = useState([]);
  const [deliveryFilesLoading, setDeliveryFilesLoading] = useState(false);
  const [billingDocsLoading, setBillingDocsLoading] = useState(false);
  const [billingDocs, setBillingDocs] = useState([]);
  const [quoteSignatureForm, setQuoteSignatureForm] = useState({});
  const [signingQuoteId, setSigningQuoteId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [projectFormMode, setProjectFormMode] = useState("create");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFormData, setProjectFormData] = useState({
    id_user: "",
    name: "",
    type: "Personnalisé",
    status: "En cours",
    progress: 0,
    description: "",
    start_date: "",
    end_date: "",
  });
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [adminStatsNotice, setAdminStatsNotice] = useState("");
  const [unreadReplies, setUnreadReplies] = useState(0);

  const orderTimelineSteps = [
    { key: "pending", label: "Demande" },
    { key: "paid", label: "Validation" },
    { key: "in_progress", label: "Production" },
    { key: "delivered", label: "Livraison" },
  ];

  const getOrderStepIndex = (status) => {
    const normalized = String(status || "pending").toLowerCase();
    if (normalized === "cancelled" || normalized === "failed") return 0;
    const idx = orderTimelineSteps.findIndex((s) => s.key === normalized);
    return idx === -1 ? 0 : idx;
  };

  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  // ...autres hooks d'état...

  // Déclaration de StatCard à l'intérieur du composant
  const StatCard = ({ icon: Icon, title, value, trend, color }) => (
    <div
      className={`p-6 rounded-2xl border shadow-sm ${isDark ? "bg-[#0b0b0c] border-white/10 shadow-black/20" : "bg-white border-gray-200 shadow-gray-200/60"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <div className="flex items-center gap-1 text-sm text-rose-500">
            <TrendingUp className="w-4 h-4" />
            <span>{trend}</span>
          </div>
        )}
      </div>
      <h3
        className={`text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
      >
        {title}
      </h3>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );

  // États pour les stats admin
  const [stats, setStats] = useState({
    users: 0,
    orders: 0,
    revenue: 0,
    messages: 0,
  });
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statsMode, setStatsMode] = useState("monthly");
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);

  // Activer l'onglet via ?tab=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const validTabs =
      user?.id_role === 3
        ? ["settings", "orders"]
        : [
            "dashboard",
            "orders",
            "projects",
            "messages",
            "quotes",
            "invoices",
            "settings",
          ];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
    if (!tab && user?.id_role === 3 && !validTabs.includes(activeTab)) {
      setActiveTab("settings");
    }
  }, [location.search, user]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        email: user.email || "",
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        nickname: user.nickname || "",
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (activeTab !== "projects") return;
      try {
        setProjectsLoading(true);
        if (user?.id_role === 3) {
          const response = await api.get("/projects/creator");
          setProjects(response.data?.projects || []);

          const usersResponse = await api.get("/users");
          const allUsers = usersResponse.data?.data || usersResponse.data || [];
          const currentId = user?.id_user || user?.id;
          const filteredUsers = allUsers.filter(
            (u) => (u.id_user || u.id) !== currentId,
          );
          setUsersList(filteredUsers);
        } else {
          const response = await api.get("/projects/my");
          setProjects(response.data?.projects || []);
        }
      } catch (error) {
        console.error("Erreur chargement projets:", error);
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };

    const fetchPurchasedFiles = async () => {
      if (activeTab !== "files") return;
      try {
        setFilesLoading(true);
        const response = await api.get("/purchased-files");
        setPurchasedFiles(response.data?.files || []);
      } catch (error) {
        console.error("Erreur chargement fichiers:", error);
        setPurchasedFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };

    fetchProjects();
    fetchPurchasedFiles();
  }, [activeTab, user]);

  useEffect(() => {
    const fetchBillingDocuments = async () => {
      if (activeTab !== "invoices" && activeTab !== "quotes") return;

      try {
        setBillingDocsLoading(true);
        const response = await api.get("/billing-documents/my");
        setBillingDocs(response.data?.documents || []);
      } catch (error) {
        console.error("Erreur chargement devis/factures:", error);
        setBillingDocs([]);
      } finally {
        setBillingDocsLoading(false);
      }
    };

    fetchBillingDocuments();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "deliveries") return;
    const fetchDeliveryFiles = async () => {
      try {
        setDeliveryFilesLoading(true);
        const res = await api.get("/delivery-files/my");
        setDeliveryFiles(res.data?.files || []);
      } catch (err) {
        console.error("Erreur chargement livraisons:", err);
        setDeliveryFiles([]);
      } finally {
        setDeliveryFilesLoading(false);
      }
    };
    fetchDeliveryFiles();
  }, [activeTab]);

  const handleDownloadBillingDocument = async (doc) => {
    try {
      const response = await api.get(
        `/billing-documents/${doc.id_billing_document}/download`,
        { responseType: "blob" },
      );

      const contentDisposition =
        response.headers?.["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(
        /filename=\"?([^\";]+)\"?/i,
      );
      const filename =
        filenameMatch?.[1] || `${doc.document_number || "document"}.pdf`;

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur telechargement document:", error);
      toast.error("Impossible de telecharger ce document.");
    }
  };

  const handleSignQuote = async (doc) => {
    const form = quoteSignatureForm[doc.id_billing_document] || {};
    const signatureName = String(form.signatureName || "").trim();
    const signatureData = form.signatureData || null;
    const acceptTerms = Boolean(form.acceptTerms);

    if (!signatureName) {
      toast.error("Entre ton nom complet pour signer le devis.");
      return;
    }

    if (!acceptTerms) {
      toast.error("Tu dois accepter les conditions pour signer.");
      return;
    }

    if (!signatureData) {
      toast.error("Merci de dessiner ta signature dans le cadre prévu.");
      return;
    }

    try {
      setSigningQuoteId(doc.id_billing_document);
      await api.post(`/billing-documents/${doc.id_billing_document}/sign`, {
        signature_name: signatureName,
        signature_data: signatureData,
        accept_terms: true,
      });

      setBillingDocs((prev) =>
        prev.map((d) =>
          d.id_billing_document === doc.id_billing_document
            ? {
                ...d,
                is_signed: true,
                signature_name: signatureName,
                signed_at: new Date().toISOString(),
                signature_data: signatureData,
              }
            : d,
        ),
      );

      toast.success("Devis signé. Redirection vers le paiement…");

      // Créer la session Stripe et rediriger
      const checkoutRes = await api.post(
        `/billing-documents/${doc.id_billing_document}/checkout`,
      );
      if (checkoutRes.data?.success && checkoutRes.data?.url) {
        window.location.href = checkoutRes.data.url;
      } else {
        toast.error(
          checkoutRes.data?.message ||
            "Impossible d'initialiser le paiement. Tu peux réessayer depuis ton espace.",
        );
      }
    } catch (error) {
      console.error("Erreur signature devis:", error);
      const message =
        error?.response?.data?.message ||
        "Impossible de signer le devis pour le moment.";
      toast.error(message);
    } finally {
      setSigningQuoteId(null);
    }
  };

  const handleCheckout = async (doc) => {
    try {
      const res = await api.post(
        `/billing-documents/${doc.id_billing_document}/checkout`,
      );
      if (res.data?.success && res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error(
          res.data?.message || "Impossible d'initialiser le paiement.",
        );
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Erreur lors de la redirection vers le paiement.",
      );
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;
      if (activeTab !== "orders" && activeTab !== "dashboard") return;

      try {
        setOrdersLoading(true);
        const endpoint = user?.id_role === 3 ? "/admin/orders" : "/orders";
        const response = await api.get(endpoint);
        const data = response.data?.data || response.data || [];
        setOrders(data);
      } catch (error) {
        console.error("Erreur chargement commandes:", error);
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [activeTab, user]);

  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user || activeTab !== "dashboard") return;

      try {
        setActivityLoading(true);
        const ordersEndpoint =
          user?.id_role === 3 ? "/admin/orders" : "/orders";

        const [ordersResponse, filesResponse, conversationsResponse] =
          await Promise.all([
            api.get(ordersEndpoint),
            api.get("/purchased-files"),
            api.get("/conversations"),
          ]);

        const orderItems = (ordersResponse.data?.data || []).map((order) => ({
          type: "order",
          title: `Commande ${order.reference || order.id || ""}`.trim(),
          date: order.created_at,
          timeValue: parseFrenchDateTime(order.created_at),
        }));

        const fileItems = (filesResponse.data?.files || []).map((file) => ({
          type: "file",
          title: `Fichier disponible - ${file.product_name || "Produit"}`,
          date: file.purchased_at,
          timeValue: parseFrenchDateTime(file.purchased_at),
        }));

        const messageItems = (
          conversationsResponse.data?.conversations || []
        ).map((conversation) => ({
          type: "message",
          title: `Nouveau message - ${conversation?.other_user?.nickname || "Support"}`,
          date: conversation?.last_message?.created_at,
          timeValue: parseFrenchDateTime(
            conversation?.last_message?.created_at,
          ),
        }));

        const merged = [...orderItems, ...fileItems, ...messageItems]
          .sort((a, b) => b.timeValue - a.timeValue)
          .slice(0, 6);

        setRecentActivity(merged);
      } catch (error) {
        console.error("Erreur chargement activite:", error);
        setRecentActivity([]);
      } finally {
        setActivityLoading(false);
      }
    };

    fetchRecentActivity();
  }, [activeTab, user]);

  const handleCreateProject = () => {
    setProjectFormMode("create");
    setSelectedProject(null);
    setProjectFormData({
      id_user: "",
      name: "",
      type: "Personnalisé",
      status: "En cours",
      progress: 0,
      description: "",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
    });
    setProjectModalOpen(true);
  };

  const handleEditProject = (project) => {
    setProjectFormMode("edit");
    setSelectedProject(project);
    setProjectFormData({
      id_user: project.id_user,
      name: project.name || "",
      type: project.type || "Personnalisé",
      status: project.status || "En cours",
      progress: project.progress || 0,
      description: project.description || "",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
    });
    setProjectModalOpen(true);
  };

  const handleSaveProject = async (e) => {
    e.preventDefault();

    try {
      if (!projectFormData.id_user) {
        toast.error("Veuillez sélectionner un client");
        return;
      }
      if (!projectFormData.name || !projectFormData.name.trim()) {
        toast.error("Veuillez saisir un nom de projet");
        return;
      }

      if (projectFormMode === "create") {
        await api.post("/projects", projectFormData);
        toast.success("Projet créé avec succès !");
      } else {
        await api.put(
          `/projects/${selectedProject.id_project}`,
          projectFormData,
        );
        toast.success("Projet mis à jour avec succès !");
      }

      setProjectModalOpen(false);
      const response = await api.get("/projects/creator");
      setProjects(response.data?.projects || []);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du projet:", error);
      const errorMsg = error.response?.data?.message || error.message;
      toast.error("Erreur: " + errorMsg);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce projet ?")) {
      return;
    }

    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Projet supprimé");
      const response = await api.get("/projects/creator");
      setProjects(response.data?.projects || []);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer le projet");
    }
  };

  // Charger les statistiques admin
  useEffect(() => {
    if (user?.id_role !== 3) return; // Seulement pour les admins

    const fetchAdminStats = async () => {
      try {
        setLoading(true);
        // Stats mensuelles ou globales
        const statsResponse =
          statsMode === "global"
            ? await api.get("/admin/stats/global")
            : await api.get(
                `/admin/stats/monthly?month=${selectedMonth}&year=${selectedYear}`,
              );

        if (statsResponse.data.success) {
          setMonthlyStats(statsResponse.data);
          setAdminStatsNotice("");
          setStats({
            users: statsResponse.data.stats.new_users,
            orders: statsResponse.data.stats.orders_count,
            revenue: statsResponse.data.stats.total_revenue,
            messages: statsResponse.data.stats.new_messages,
          });
          setCreatorsEarnings(statsResponse.data.creators || []);
        } else {
          setMonthlyStats(null);
          setAdminStatsNotice("Pas de statistiques pour le moment.");
        }

        // Utilisateurs récents (séparé pour éviter de bloquer les stats)
        try {
          const usersResponse = await api.get("/admin/users");
          setRecentUsers(usersResponse.data?.data?.slice(0, 5) || []);
        } catch {}

        // Commandes récentes (optionnel, si la table existe)
        try {
          const ordersResponse = await api.get("/admin/billing-documents");
          const docs = ordersResponse.data?.documents || [];
          setRecentOrders(docs.slice(0, 5).map(d => ({
            id: d.id_billing_document,
            reference: d.document_number,
            status: d.status,
            user_name: d.client_name,
            product_name: d.service_name,
            total: d.amount_ttc,
          })));
        } catch {}
      } catch (error) {
        console.error("Erreur chargement stats admin:", error);
        const apiMessage =
          error.response?.data?.message || error.response?.data?.error;
        setMonthlyStats(null);
        setAdminStatsNotice(
          apiMessage || "Pas de statistiques pour le moment.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === "settings") {
      fetchAdminStats();
    }
  }, [user, activeTab, selectedMonth, selectedYear, statsMode]);

  // Charger le nombre de messages non lus (conversations)
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (user?.id_role === 3) return; // Pas pour les admins
      try {
        const response = await api.get("/conversations/unread-count");
        if (response.data.success) {
          setUnreadReplies(response.data.unread_count || 0);
        }
      } catch (error) {
        console.error("Erreur comptage messages:", error);
      }
    };

    fetchUnreadCount();

    // Écouter les mises à jour de messages
    const handleMessagesUpdate = () => {
      fetchUnreadCount();
    };
    window.addEventListener("messagesUpdated", handleMessagesUpdate);

    // Recharger toutes les 30 secondes
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("messagesUpdated", handleMessagesUpdate);
    };
  }, [user, activeTab]);

  const handleProfileChange = (field) => (e) => {
    setProfileForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    const emailChanged = profileForm.email !== (user.email || "");
    try {
      setProfileSaving(true);
      const response = await api.put(`/users/${user.id_user || user.id}`, {
        email: profileForm.email,
        firstname: profileForm.firstname,
        lastname: profileForm.lastname,
        nickname: profileForm.nickname,
      });

      if (emailChanged) {
        toast.success("Email modifié. Un email de vérification a été envoyé.");
        setEmailNotice(
          "Email modifié : vérifiez votre boîte mail pour valider votre nouvelle adresse.",
        );
      } else {
        toast.success("Informations mises à jour.");
        setEmailNotice("");
      }

      await refreshUser();

      // Compatibilité si l’API renvoie user directement
      if (response?.data?.user) {
        setProfileForm({
          email: response.data.user.email || "",
          firstname: response.data.user.firstname || "",
          lastname: response.data.user.lastname || "",
          nickname: response.data.user.nickname || "",
        });
      }
    } catch (error) {
      console.error("Erreur mise à jour profil:", error);
      toast.error(
        error.response?.data?.message || "Erreur lors de la mise à jour.",
      );
    } finally {
      setProfileSaving(false);
    }
  };

  // Retirer le lien d'un projet (remettre à vide)
  const handleRemoveProjectLink = async (projectId) => {
    try {
      await api.put(`/projects/${projectId}`, { link: "" });
      toast.success("Lien retiré du projet.");
      // Rafraîchir la liste
      const response = await api.get(
        user?.id_role === 3 ? "/projects/creator" : "/projects/my",
      );
      setProjects(response.data?.projects || []);
    } catch (error) {
      toast.error("Erreur lors du retrait du lien.");
    }
  };

  return (
    <div
      className={`flex-1 flex flex-col ${isDark ? "bg-[#0f1114] text-white" : "bg-slate-50 text-gray-800"}`}
    >
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <SectionTitle
          title={user?.id_role === 3 ? "Administration" : "Mon espace"}
          subtitle={
            user?.id_role === 3
              ? "Gestion des utilisateurs, projets et messages"
              : "Gérez vos commandes, projets et support"
          }
          align="left"
        />

        {/* Panneau admin métier (si admin) */}
        {adminPanel}

        {/* Navigation par onglets */}
        <div
          className={`mb-8 overflow-x-auto ${isDark ? "bg-[#0b0b0c] border border-white/6" : "bg-white border border-gray-100"} rounded-lg`}
        >
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap select-none relative ${
                  activeTab === tab.key
                    ? isDark
                      ? "bg-rose-500/20 text-rose-400 border-b-2 border-rose-500"
                      : "bg-rose-50 text-rose-600 border-b-2 border-rose-600"
                    : isDark
                      ? "text-white/70 hover:bg-white/5"
                      : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.key === "messages" && unreadReplies > 0 && (
                  <span className="relative flex h-5 w-5 ml-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-5 w-5 bg-gradient-to-br from-rose-500 to-pink-600 items-center justify-center text-white text-xs font-bold shadow-lg">
                      {unreadReplies > 9 ? "9+" : unreadReplies}
                    </span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Contenu des onglets */}
        <div>
          {/* Dashboard */}
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              <Card>
                <h3 className="text-xl font-semibold mb-3">
                  Bienvenue {user?.firstname || user?.nickname}
                </h3>
                <p className={isDark ? "text-white/70" : "text-gray-600"}>
                  Retrouvez ici vos commandes, projets et messages de support.
                </p>
              </Card>

              <Card>
                <h3 className="text-lg font-semibold mb-3">Activité récente</h3>
                {activityLoading ? (
                  <p className={isDark ? "text-white/60" : "text-gray-600"}>
                    Chargement...
                  </p>
                ) : recentActivity.length === 0 ? (
                  <p className={isDark ? "text-white/60" : "text-gray-600"}>
                    Aucune activite recente.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {recentActivity.map((item, idx) => (
                      <div
                        key={`${item.type}-${idx}`}
                        className={`p-3 rounded ${isDark ? "" : "bg-gray-50"}`}
                        style={
                          isDark ? { backgroundColor: "#111116" } : undefined
                        }
                      >
                        <p className="font-medium">{item.title}</p>
                        <p
                          className={`text-sm ${isDark ? "text-white/50" : "text-gray-500"}`}
                        >
                          {item.date}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Commandes */}
          {activeTab === "orders" && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">Mes commandes</h3>
              {ordersLoading ? (
                <p className={isDark ? "text-white/60" : "text-gray-600"}>
                  Chargement...
                </p>
              ) : orders.length === 0 ? (
                <p className={isDark ? "text-white/60" : "text-gray-600"}>
                  Aucune commande pour le moment.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead
                      className={`${isDark ? "bg-white/5" : "bg-gray-50"}`}
                    >
                      <tr>
                        <th className="text-left p-3 font-semibold">
                          Référence
                        </th>
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-left p-3 font-semibold">Produit</th>
                        <th className="text-left p-3 font-semibold">Statut</th>
                        <th className="text-left p-3 font-semibold">Prix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => {
                        const items = order.items || [];
                        const firstItem = items[0];
                        const isWebsiteOrder =
                          order.order_type === "custom_website_order";
                        const itemLabel =
                          (isWebsiteOrder && order.plan_name) ||
                          firstItem?.product_name ||
                          "Produit";
                        const extraCount =
                          items.length > 1 ? ` +${items.length - 1}` : "";
                        const statusLabel = order.status || "En attente";
                        const activeStep = getOrderStepIndex(statusLabel);

                        return (
                          <tr
                            key={order.id}
                            className={`border-t ${isDark ? "border-white/10" : "border-gray-200"}`}
                          >
                            <td className="p-3 font-mono text-sm">
                              {order.reference || `#${order.id}`}
                            </td>
                            <td className="p-3">{order.created_at}</td>
                            <td className="p-3">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span>
                                    {itemLabel}
                                    {extraCount}
                                  </span>
                                  {isWebsiteOrder && (
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                        isDark
                                          ? "bg-violet-500/20 text-violet-300"
                                          : "bg-violet-100 text-violet-700"
                                      }`}
                                    >
                                      Commande site
                                    </span>
                                  )}
                                </div>
                                {isWebsiteOrder && order.billing_mode && (
                                  <span
                                    className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Mode: {order.billing_mode}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex flex-col gap-2">
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-medium w-fit ${statusColors[statusLabel] || (isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-700")}`}
                                >
                                  {statusLabel}
                                </span>
                                {isWebsiteOrder && (
                                  <div className="flex items-center gap-1.5">
                                    {orderTimelineSteps.map((step, idx) => (
                                      <div
                                        key={`${order.id}-${step.key}`}
                                        className="flex items-center gap-1.5"
                                      >
                                        <span
                                          className={`h-2 w-2 rounded-full ${
                                            idx <= activeStep
                                              ? "bg-rose-500"
                                              : isDark
                                                ? "bg-white/20"
                                                : "bg-gray-300"
                                          }`}
                                        />
                                        <span
                                          className={`text-[10px] ${idx <= activeStep ? (isDark ? "text-white" : "text-gray-800") : isDark ? "text-gray-500" : "text-gray-400"}`}
                                        >
                                          {step.label}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="p-3 font-semibold">
                              {order.total}€
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Projets */}
          {activeTab === "projects" && (
            <div className="space-y-4">
              <Card>
                <h3 className="text-xl font-semibold mb-4">Mes projets</h3>
                {user?.id_role === 3 && (
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={handleCreateProject}
                      className={`px-4 py-2 rounded-lg font-medium transition ${isDark ? "bg-rose-500 hover:bg-rose-600 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}`}
                    >
                      Nouveau projet
                    </button>
                  </div>
                )}
                {projectsLoading ? (
                  <p className={isDark ? "text-white/70" : "text-gray-600"}>
                    Chargement...
                  </p>
                ) : projects.length === 0 ? (
                  <p className={isDark ? "text-white/70" : "text-gray-600"}>
                    Aucun projet pour le moment.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead
                        className={`${isDark ? "bg-white/5" : "bg-gray-50"}`}
                      >
                        <tr>
                          <th className="text-left p-3 font-semibold">
                            Projet
                          </th>
                          {user?.id_role === 3 && (
                            <th className="text-left p-3 font-semibold">
                              Client
                            </th>
                          )}
                          <th className="text-left p-3 font-semibold">Type</th>
                          <th className="text-left p-3 font-semibold">
                            Statut
                          </th>
                          <th className="text-left p-3 font-semibold">
                            Progression
                          </th>
                          {user?.id_role === 3 && (
                            <th className="text-right p-3 font-semibold">
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {projects.map((project) => (
                          <tr
                            key={project.id_project}
                            className={`border-t ${isDark ? "border-white/10" : "border-gray-200"}`}
                          >
                            <td className="p-3">
                              <div className="font-medium text-base mb-1">
                                {project.name}
                              </div>
                              {project.description && (
                                <div
                                  className={`text-xs mb-1 ${isDark ? "text-white/50" : "text-gray-500"}`}
                                >
                                  {project.description}
                                </div>
                              )}
                              {/* Images protégées du projet */}
                              {Array.isArray(project.images) &&
                                project.images.length > 0 && (
                                  <div className="flex gap-2 mt-1">
                                    {project.images.map((img, idx) => (
                                      <img
                                        key={idx}
                                        src={img.secure_url || img.url || img}
                                        alt={
                                          project.name + " image " + (idx + 1)
                                        }
                                        className="w-12 h-12 object-cover rounded shadow border border-white/10"
                                        loading="lazy"
                                      />
                                    ))}
                                  </div>
                                )}
                              {/* Lien du projet */}
                              {project.link && (
                                <div className="mt-1 flex items-center gap-2">
                                  <a
                                    href={
                                      project.link.startsWith("http")
                                        ? project.link
                                        : undefined
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`text-xs underline ${isDark ? "text-rose-300" : "text-rose-600"}`}
                                  >
                                    {project.link}
                                  </a>
                                  {/* Si le lien est un mot (pas une URL), bouton retirer */}
                                  {!project.link.startsWith("http") && (
                                    <button
                                      onClick={() =>
                                        handleRemoveProjectLink(
                                          project.id_project,
                                        )
                                      }
                                      className="ml-1 px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                    >
                                      Retirer
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            {user?.id_role === 3 && (
                              <td className="p-3">
                                {project.user ? (
                                  <div>
                                    <div className="font-medium">
                                      {project.user.name ||
                                        project.user.first_name}
                                    </div>
                                    <div
                                      className={`text-xs ${isDark ? "text-white/50" : "text-gray-500"}`}
                                    >
                                      {project.user.email}
                                    </div>
                                  </div>
                                ) : (
                                  <span
                                    className={
                                      isDark ? "text-white/50" : "text-gray-500"
                                    }
                                  >
                                    N/A
                                  </span>
                                )}
                              </td>
                            )}
                            <td className="p-3">{project.type || "-"}</td>
                            <td className="p-3">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[project.status] || (isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-700")}`}
                              >
                                {project.status}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-32 h-2 rounded-full ${isDark ? "bg-white/10" : "bg-gray-200"}`}
                                >
                                  <div
                                    className={`h-full rounded-full ${isDark ? "bg-rose-500" : "bg-rose-600"}`}
                                    style={{
                                      width: `${project.progress || 0}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-semibold">
                                  {project.progress || 0}%
                                </span>
                              </div>
                            </td>
                            {user?.id_role === 3 && (
                              <td className="p-3 text-right">
                                <div className="inline-flex gap-2">
                                  <button
                                    onClick={() => handleEditProject(project)}
                                    className={`px-3 py-1 rounded text-sm ${isDark ? "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30" : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"}`}
                                  >
                                    Modifier
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteProject(project.id_project)
                                    }
                                    className={`px-3 py-1 rounded text-sm ${isDark ? "bg-red-500/20 text-red-300 hover:bg-red-500/30" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                                  >
                                    Supprimer
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Messages (Support + historique) */}
          {activeTab === "messages" && (
            <div className="space-y-6">
              <Card>
                <h3 className="text-xl font-semibold mb-4">Vos messages</h3>
                <Messages />
              </Card>
            </div>
          )}

          {/* Fichiers */}
          {activeTab === "files" && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">
                Mes fichiers téléchargeables
              </h3>

              {filesLoading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : purchasedFiles.length === 0 ? (
                <div
                  className={`text-center py-8 ${isDark ? "text-white/70" : "text-gray-600"}`}
                >
                  <p className="mb-2">
                    Aucun fichier disponible pour le moment.
                  </p>
                  <p className="text-sm">
                    Les fichiers de vos achats apparaîtront ici après paiement.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {purchasedFiles.map((file, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">
                            {file.product_name || "Fichier"}
                          </h4>
                          <p
                            className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                          >
                            Commande: {file.order_number} -{" "}
                            {new Date(file.purchased_at).toLocaleDateString(
                              "fr-FR",
                            )}
                          </p>
                          {file.original_filename && (
                            <p
                              className={`text-xs mt-1 ${isDark ? "text-white/50" : "text-gray-500"}`}
                            >
                              📄 {file.original_filename}
                            </p>
                          )}
                        </div>
                        <a
                          href={`${api.defaults.baseURL}/purchased-files/${file.id}/download`}
                          download
                          className={`px-4 py-2 rounded font-medium transition ${isDark ? "bg-rose-500 hover:bg-rose-600" : "bg-rose-600 hover:bg-rose-700"} text-white`}
                        >
                          ⬇️ Télécharger
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Factures */}
          {/* ─── ONGLET DEVIS ─────────────────────────────────────── */}
          {activeTab === "quotes" && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">Mes devis</h3>
              {billingDocsLoading ? (
                <div className="text-center py-6">Chargement...</div>
              ) : billingDocs.filter((d) => d.document_type === "quote")
                  .length === 0 ? (
                <p className={isDark ? "text-white/70" : "text-gray-600"}>
                  Aucun devis disponible pour le moment.
                </p>
              ) : (
                <div className="space-y-3">
                  {billingDocs
                    .filter((d) => d.document_type === "quote")
                    .map((doc) => (
                      <div
                        key={doc.id_billing_document}
                        className={`p-4 rounded-lg border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-wider font-bold mb-1">
                              {doc.document_type === "quote"
                                ? "Devis"
                                : "Facture"}
                            </p>
                            <p className="font-semibold">
                              {doc.document_number}
                            </p>
                            <p
                              className={`text-sm ${isDark ? "text-white/70" : "text-gray-600"}`}
                            >
                              {doc.service_name}
                            </p>
                            <p
                              className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}
                            >
                              {new Date(doc.created_at).toLocaleDateString(
                                "fr-FR",
                              )}{" "}
                              • {Number(doc.amount_ttc || 0).toFixed(2)} € TTC
                              {doc.billing_mode === "monthly" &&
                              doc.installments
                                ? ` • ${Number(doc.monthly_amount || 0).toFixed(2)} € x${doc.installments}`
                                : ""}
                            </p>
                            {doc.document_type === "quote" && (
                              <div className="mt-1">
                                {doc.is_signed ? (
                                  <>
                                    <p className="text-xs text-green-400">
                                      {`Signé électroniquement par ${doc.signature_name || "client"}${doc.signed_at ? ` le ${new Date(doc.signed_at).toLocaleDateString("fr-FR")}` : ""}`}
                                    </p>
                                    {doc.signature_data && (
                                      <div className="mt-2">
                                        <span className="block text-xs text-gray-400 mb-1">
                                          Empreinte de signature :
                                        </span>
                                        <img
                                          src={doc.signature_data}
                                          alt="Signature manuscrite"
                                          style={{
                                            maxWidth: 240,
                                            maxHeight: 90,
                                            border: "1px solid #ddd",
                                            background: "#fff",
                                            borderRadius: 6,
                                          }}
                                        />
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <p
                                    className={`text-xs ${isDark ? "text-amber-300" : "text-amber-700"}`}
                                  >
                                    En attente de signature électronique
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={() => handleDownloadBillingDocument(doc)}
                              className={`px-4 py-2 rounded font-medium transition ${isDark ? "bg-rose-500 hover:bg-rose-600" : "bg-rose-600 hover:bg-rose-700"} text-white`}
                            >
                              ⬇️ Télécharger
                            </button>
                            {/* Bouton payer : si signé et pas encore entièrement payé */}
                            {doc.is_signed && doc.status !== "paid" && (
                              <button
                                onClick={() => handleCheckout(doc)}
                                className="px-4 py-2 rounded font-medium text-white transition"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
                                }}
                              >
                                {doc.billing_mode === "monthly"
                                  ? `💳 Payer la mensualité ${(doc.paid_installments || 0) + 1}/${doc.installments}`
                                  : "💳 Payer en ligne"}
                              </button>
                            )}
                            {doc.status === "paid" && (
                              <span className="px-3 py-2 rounded text-sm font-semibold bg-green-500/15 text-green-400 border border-green-500/20">
                                ✅ Payé
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Suivi mensualités */}
                        {doc.billing_mode === "monthly" &&
                          doc.installments > 1 && (
                            <div
                              className={`mt-3 p-3 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-100"}`}
                            >
                              <p
                                className={`text-xs font-semibold mb-2 ${isDark ? "text-white/60" : "text-gray-600"}`}
                              >
                                Mensualités — {doc.paid_installments || 0}/
                                {doc.installments} payée(s)
                              </p>
                              <div className="flex gap-1.5 flex-wrap">
                                {Array.from({ length: doc.installments }).map(
                                  (_, i) => (
                                    <div
                                      key={i}
                                      className={`flex items-center justify-center rounded text-xs font-bold px-2 py-1 ${
                                        i < (doc.paid_installments || 0)
                                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                          : isDark
                                            ? "bg-white/5 text-white/40 border border-white/10"
                                            : "bg-gray-200 text-gray-400 border border-gray-300"
                                      }`}
                                    >
                                      {i < (doc.paid_installments || 0)
                                        ? "✓"
                                        : i + 1}
                                      <span className="ml-1">
                                        {Number(
                                          doc.monthly_amount || 0,
                                        ).toFixed(0)}
                                        €
                                      </span>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                        {doc.document_type === "quote" && !doc.is_signed && (
                          <div
                            className={`mt-3 p-3 rounded-lg border space-y-2 ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}
                          >
                            <p
                              className={`text-xs font-semibold ${isDark ? "text-white/70" : "text-gray-700"}`}
                            >
                              Signature électronique du devis
                            </p>

                            <input
                              type="text"
                              value={
                                quoteSignatureForm[doc.id_billing_document]
                                  ?.signatureName || ""
                              }
                              onChange={(e) =>
                                setQuoteSignatureForm((prev) => ({
                                  ...prev,
                                  [doc.id_billing_document]: {
                                    ...prev[doc.id_billing_document],
                                    signatureName: e.target.value,
                                  },
                                }))
                              }
                              className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                              placeholder="Ton nom complet"
                            />

                            <div className="mt-2">
                              <SignatureCanvas
                                value={
                                  quoteSignatureForm[doc.id_billing_document]
                                    ?.signatureData || null
                                }
                                onChange={(img) =>
                                  setQuoteSignatureForm((prev) => ({
                                    ...prev,
                                    [doc.id_billing_document]: {
                                      ...prev[doc.id_billing_document],
                                      signatureData: img,
                                    },
                                  }))
                                }
                                width={320}
                                height={120}
                                disabled={
                                  signingQuoteId === doc.id_billing_document
                                }
                              />
                              <span className="block text-xs text-gray-500 mt-1">
                                Signe ici (au doigt ou à la souris)
                              </span>
                            </div>

                            <label
                              className={`flex items-center gap-2 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(
                                  quoteSignatureForm[doc.id_billing_document]
                                    ?.acceptTerms,
                                )}
                                onChange={(e) =>
                                  setQuoteSignatureForm((prev) => ({
                                    ...prev,
                                    [doc.id_billing_document]: {
                                      ...prev[doc.id_billing_document],
                                      acceptTerms: e.target.checked,
                                    },
                                  }))
                                }
                              />
                              J'accepte les
                              <button
                                type="button"
                                className="underline text-blue-600 ml-1"
                                onClick={() => setShowCgvModal(true)}
                              >
                                Conditions Générales de Vente
                              </button>
                              et je signe ce devis électroniquement.
                            </label>
                            {/* Modal CGV */}
                            {showCgvModal && (
                              <Modal
                                open={showCgvModal}
                                onClose={() => setShowCgvModal(false)}
                                title="Conditions Générales de Vente (CGV)"
                              >
                                <div className="prose max-w-none text-sm text-gray-800">
                                  <h2 className="text-gray-900 font-bold text-base mb-2">
                                    Conditions Générales de Vente (CGV)
                                  </h2>
                                  <p className="text-gray-700">
                                    Toute commande implique l'adhésion sans
                                    réserve du client aux présentes CGV. Les
                                    prestations proposées (création de site
                                    internet, hébergement, maintenance, etc.)
                                    font l'objet d'un devis personnalisé. Le
                                    paiement peut s'effectuer mensuellement ou
                                    en une fois selon l'accord convenu. Pour
                                    toute question, contactez-moi.
                                  </p>
                                  <p className="text-xs text-gray-500 mt-4">
                                    Dernière mise à jour : avril 2026
                                  </p>
                                </div>
                              </Modal>
                            )}

                            <button
                              onClick={() => handleSignQuote(doc)}
                              disabled={
                                signingQuoteId === doc.id_billing_document
                              }
                              className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition disabled:opacity-60 ${isDark ? "bg-indigo-500 hover:bg-indigo-600" : "bg-indigo-600 hover:bg-indigo-700"}`}
                            >
                              {signingQuoteId === doc.id_billing_document
                                ? "Signature..."
                                : "Signer électroniquement"}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </Card>
          )}

          {/* Livraisons */}
          {activeTab === "deliveries" && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">
                📦 Mes fichiers livrés
              </h3>
              {deliveryFilesLoading ? (
                <div className="text-center py-6">Chargement…</div>
              ) : deliveryFiles.length === 0 ? (
                <p className={isDark ? "text-white/70" : "text-gray-600"}>
                  Aucun fichier livré pour le moment. Dès qu'un fichier vous
                  sera envoyé, il apparaîtra ici.
                </p>
              ) : (
                <div className="space-y-3">
                  {deliveryFiles.map((file) => (
                    <div
                      key={file.id}
                      className={`p-4 rounded-lg border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">
                            {file.label || file.original_filename}
                          </p>
                          <p
                            className={`text-sm truncate mt-0.5 ${isDark ? "text-white/60" : "text-gray-500"}`}
                          >
                            📄 {file.original_filename}
                          </p>
                          <p
                            className={`text-xs mt-1 ${isDark ? "text-white/40" : "text-gray-400"}`}
                          >
                            {file.file_size} · Reçu le {file.created_at}
                            {file.download_count > 0
                              ? ` · Téléchargé ${file.download_count} fois`
                              : ""}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const res = await api.get(
                                `/delivery-files/${file.id}/download`,
                                { responseType: "blob" },
                              );
                              const contentDisposition =
                                res.headers?.["content-disposition"] || "";
                              const match = contentDisposition.match(
                                /filename="?([^";]+)"?/i,
                              );
                              const filename =
                                match?.[1] || file.original_filename;
                              const url = URL.createObjectURL(
                                new Blob([res.data]),
                              );
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                              setDeliveryFiles((prev) =>
                                prev.map((f) =>
                                  f.id === file.id
                                    ? {
                                        ...f,
                                        download_count:
                                          (f.download_count || 0) + 1,
                                      }
                                    : f,
                                ),
                              );
                            } catch {
                              toast.error(
                                "Impossible de télécharger le fichier.",
                              );
                            }
                          }}
                          className={`px-4 py-2 rounded font-medium transition shrink-0 ${isDark ? "bg-rose-500 hover:bg-rose-600" : "bg-rose-600 hover:bg-rose-700"} text-white`}
                        >
                          ⬇️ Télécharger
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Paramètres */}
          {activeTab === "invoices" && (
            <Card>
              <h3 className="text-xl font-semibold mb-4">Mes factures</h3>
              {billingDocsLoading ? (
                <div className="text-center py-6">Chargement...</div>
              ) : billingDocs.filter((d) => d.document_type === "invoice")
                  .length === 0 ? (
                <p className={isDark ? "text-white/70" : "text-gray-600"}>
                  Aucune facture disponible pour le moment.
                </p>
              ) : (
                <div className="space-y-3">
                  {billingDocs
                    .filter((d) => d.document_type === "invoice")
                    .map((doc) => (
                      <div
                        key={doc.id_billing_document}
                        className={`p-4 rounded-lg border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-wider font-bold mb-1 text-amber-500">
                              Facture
                            </p>
                            <p className="font-semibold">
                              {doc.document_number}
                            </p>
                            <p
                              className={`text-sm ${isDark ? "text-white/70" : "text-gray-600"}`}
                            >
                              {doc.service_name}
                            </p>
                            <p
                              className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}
                            >
                              {new Date(doc.created_at).toLocaleDateString(
                                "fr-FR",
                              )}{" "}
                              • {Number(doc.amount_ttc || 0).toFixed(2)} € TTC
                              {doc.billing_mode === "monthly" &&
                              doc.installments
                                ? ` • ${Number(doc.monthly_amount || 0).toFixed(2)} € x${doc.installments}`
                                : ""}
                            </p>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                            <button
                              onClick={() => handleDownloadBillingDocument(doc)}
                              className={`px-4 py-2 rounded font-medium transition ${isDark ? "bg-rose-500 hover:bg-rose-600" : "bg-rose-600 hover:bg-rose-700"} text-white`}
                            >
                              ⬇️ Télécharger
                            </button>
                            {doc.status !== "paid" && (
                              <button
                                onClick={() => handleCheckout(doc)}
                                className="px-4 py-2 rounded font-medium text-white transition"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
                                }}
                              >
                                {doc.billing_mode === "monthly"
                                  ? `💳 Mensualité ${(doc.paid_installments || 0) + 1}/${doc.installments}`
                                  : "💳 Payer en ligne"}
                              </button>
                            )}
                            {doc.status === "paid" && (
                              <span className="px-3 py-2 rounded text-sm font-semibold bg-green-500/15 text-green-400 border border-green-500/20">
                                ✅ Payé
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Suivi mensualités */}
                        {doc.billing_mode === "monthly" &&
                          doc.installments > 1 && (
                            <div
                              className={`mt-3 p-3 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-100"}`}
                            >
                              <p
                                className={`text-xs font-semibold mb-2 ${isDark ? "text-white/60" : "text-gray-600"}`}
                              >
                                Mensualités — {doc.paid_installments || 0}/
                                {doc.installments} payée(s)
                              </p>
                              <div className="flex gap-1.5 flex-wrap">
                                {Array.from({ length: doc.installments }).map(
                                  (_, i) => (
                                    <div
                                      key={i}
                                      className={`flex items-center justify-center rounded text-xs font-bold px-2 py-1 ${
                                        i < (doc.paid_installments || 0)
                                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                          : isDark
                                            ? "bg-white/5 text-white/40 border border-white/10"
                                            : "bg-gray-200 text-gray-400 border border-gray-300"
                                      }`}
                                    >
                                      {i < (doc.paid_installments || 0)
                                        ? "✓"
                                        : i + 1}
                                      <span className="ml-1">
                                        {Number(
                                          doc.monthly_amount || 0,
                                        ).toFixed(0)}
                                        €
                                      </span>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    ))}
                </div>
              )}
            </Card>
          )}

          {/* Paramètres */}
          {activeTab === "settings" && (
            <Card>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                <h3 className="text-xl font-semibold">
                  {user?.id_role === 3 ? "Tableau de bord" : "Mon profil"}
                </h3>
                {user?.id_role === 3 && (
                  <div
                    className={`flex items-center gap-2 rounded-full p-1 ${isDark ? "bg-white/5" : "bg-gray-100"}`}
                  >
                    <button
                      type="button"
                      onClick={() => setAdminProfileTab("stats")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                        adminProfileTab === "stats"
                          ? "bg-rose-500 text-white"
                          : isDark
                            ? "text-gray-300 hover:bg-white/5"
                            : "text-gray-600 hover:bg-white"
                      }`}
                    >
                      Statistiques
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdminProfileTab("account")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                        adminProfileTab === "account"
                          ? "bg-rose-500 text-white"
                          : isDark
                            ? "text-gray-300 hover:bg-white/5"
                            : "text-gray-600 hover:bg-white"
                      }`}
                    >
                      Compte
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-6">
                {user?.id_role === 3 && adminProfileTab === "stats" && (
                  <div className="space-y-6">
                    <div
                      className={`relative overflow-hidden p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                    >
                      <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-rose-500/10 blur-3xl"></div>
                      <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <h4 className="text-2xl font-semibold">
                            Vue d'ensemble mensuelle
                          </h4>
                          <p
                            className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                          >
                            CA, gains, taxes et activité de la période
                            sélectionnée
                          </p>
                          <div
                            className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${isDark ? "bg-white/5 text-white/70" : "bg-gray-100 text-gray-600"}`}
                          >
                            Période :{" "}
                            {statsMode === "global"
                              ? "Global"
                              : new Date(
                                  selectedYear,
                                  selectedMonth - 1,
                                  1,
                                ).toLocaleDateString("fr-FR", {
                                  month: "long",
                                  year: "numeric",
                                })}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          <div
                            className={`flex items-center gap-2 rounded-full p-1 ${isDark ? "bg-white/5" : "bg-gray-100"}`}
                          >
                            <button
                              type="button"
                              onClick={() => setStatsMode("monthly")}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                                statsMode === "monthly"
                                  ? "bg-rose-500 text-white"
                                  : isDark
                                    ? "text-gray-300 hover:bg-white/5"
                                    : "text-gray-600 hover:bg-white"
                              }`}
                            >
                              Mensuel
                            </button>
                            <button
                              type="button"
                              onClick={() => setStatsMode("global")}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                                statsMode === "global"
                                  ? "bg-rose-500 text-white"
                                  : isDark
                                    ? "text-gray-300 hover:bg-white/5"
                                    : "text-gray-600 hover:bg-white"
                              }`}
                            >
                              Global
                            </button>
                          </div>
                          <select
                            value={selectedMonth}
                            onChange={(e) =>
                              setSelectedMonth(parseInt(e.target.value))
                            }
                            disabled={statsMode === "global"}
                            className={`px-4 py-2 rounded-lg border text-sm ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300"} ${
                              statsMode === "global"
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            {[...Array(12)].map((_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {new Date(2000, i, 1).toLocaleDateString(
                                  "fr-FR",
                                  { month: "long" },
                                )}
                              </option>
                            ))}
                          </select>
                          <select
                            value={selectedYear}
                            onChange={(e) =>
                              setSelectedYear(parseInt(e.target.value))
                            }
                            disabled={statsMode === "global"}
                            className={`px-4 py-2 rounded-lg border text-sm ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300"} ${
                              statsMode === "global"
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            {[2024, 2025, 2026].map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {/* Récapitulatif agence */}
                      <div
                        className={`mt-8 p-6 rounded-xl border-2 ${isDark ? "border-rose-500/40 bg-[#1a1a1e]" : "border-rose-200 bg-rose-50"}`}
                      >
                        <h5 className="text-lg font-bold text-rose-600 mb-2 flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-rose-500" />{" "}
                          Paiements & revenus — Krayne Agency
                        </h5>
                        <ul className="space-y-2 text-sm">
                          <li>
                            <span className="font-semibold text-green-500">
                              Revenus TTC :
                            </span>{" "}
                            Montant total facturé aux clients (devis + factures)
                          </li>
                          <li>
                            <span className="font-semibold text-blue-500">
                              TVA (20%) :
                            </span>{" "}
                            À reverser sur chaque prestation facturée
                          </li>
                          <li>
                            <span className="font-semibold text-amber-500">
                              Revenus HT :
                            </span>{" "}
                            Votre gain réel après déduction de la TVA
                          </li>
                          <li>
                            <span className="font-semibold text-violet-500">
                              Paiements Stripe :
                            </span>{" "}
                            Sécurisés et tracés, déduction des frais Stripe (~1,4%)
                          </li>
                        </ul>
                        <div className="mt-4 text-xs text-gray-500">
                          <b>Astuce :</b> Consultez l'onglet Devis & Facturation dans l'administration
                          pour suivre chaque document en détail.
                        </div>
                      </div>
                    </div>

                    {loading && (
                      <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
                        <p
                          className={`mt-2 ${isDark ? "text-white/60" : "text-gray-600"}`}
                        >
                          Chargement des statistiques...
                        </p>
                      </div>
                    )}

                    {!loading && !monthlyStats && adminStatsNotice && (
                      <div
                        className={`p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                      >
                        <p
                          className={isDark ? "text-white/70" : "text-gray-600"}
                        >
                          {adminStatsNotice}
                        </p>
                      </div>
                    )}

                    {monthlyStats && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <StatCard
                            icon={Users}
                            title="Utilisateurs"
                            value={stats.users.toLocaleString()}
                            trend={formatTrend(
                              monthlyStats?.stats?.users_variation,
                            )}
                            color="bg-blue-500/10 text-blue-500"
                          />
                          <StatCard
                            icon={ShoppingBag}
                            title="Commandes"
                            value={stats.orders}
                            trend={formatTrend(
                              monthlyStats?.stats?.orders_variation,
                            )}
                            color="bg-rose-500/10 text-rose-500"
                          />
                          <StatCard
                            icon={DollarSign}
                            title="CA TTC"
                            value={`${monthlyStats.stats.total_revenue}€`}
                            trend={formatTrend(
                              monthlyStats?.stats?.revenue_variation,
                            )}
                            color="bg-green-500/10 text-green-500"
                          />
                          <StatCard
                            icon={Mail}
                            title="Messages"
                            value={stats.messages}
                            color="bg-amber-500/10 text-amber-500"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                          {/* Gain mensuel net (après déduction TVA, commission, Stripe) */}
                          <div
                            className={`p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                          >
                            <p
                              className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                            >
                              Gain mensuel net
                            </p>
                            <div className="flex items-center gap-2">
                              <p className="text-2xl font-bold text-green-500">
                                {monthlyStats.stats.net_revenue}€
                              </p>
                              {/* Flèche hausse/baisse */}
                              {monthlyStats.stats.net_revenue >
                              monthlyStats.stats.net_revenue_last_month ? (
                                <span className="flex items-center text-green-500">
                                  <svg
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M10 3l6 6H4l6-6zm0 2.828L5.414 10h9.172L10 5.828z" />
                                  </svg>
                                  <span className="ml-1 text-xs">
                                    En hausse
                                  </span>
                                </span>
                              ) : (
                                <span
                                  className="flex items-center text-red-500"
                                  style={{
                                    background: "none",
                                    boxShadow: "none",
                                  }}
                                >
                                  <svg
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M10 17l-6-6h12l-6 6zm0-2.828L14.586 10H5.414L10 14.172z" />
                                  </svg>
                                  <span className="ml-1 text-xs">
                                    En baisse
                                  </span>
                                </span>
                              )}
                            </div>
                            <p
                              className={`text-xs mt-2 ${isDark ? "text-green-400" : "text-green-600"}`}
                            >
                              Montant total correspondant à votre part créateur
                              sur les ventes enregistrées.
                            </p>
                          </div>
                          {/* TVA à reverser (ventes perso + commissions) */}
                          <div
                            className={`p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                          >
                            <p
                              className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                            >
                              TVA à reverser (20%)
                            </p>
                            <div className="space-y-1">
                              <div>
                                <span className="font-semibold text-blue-500">
                                  TVA sur ventes perso :
                                </span>{" "}
                                {monthlyStats.stats.tva_personal ?? 0}€
                              </div>
                              <div>
                                <span className="font-semibold text-blue-400">
                                  TVA sur commissions :
                                </span>{" "}
                                {monthlyStats.stats.tva_commission ?? 0}€
                              </div>
                              <div className="mt-2 font-bold text-blue-600">
                                Total TVA à reverser :{" "}
                                {(monthlyStats.stats.tva_personal ?? 0) +
                                  (monthlyStats.stats.tva_commission ?? 0)}
                                €
                              </div>
                            </div>
                            <p
                              className={`text-xs mt-2 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                            >
                              Somme des TVA sur tes ventes et sur les
                              commissions prélevées.
                            </p>
                          </div>
                          {/* Somme gagnée avec les ventes */}
                          <div
                            className={`p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                          >
                            <p
                              className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                            >
                              Gains sur ventes
                            </p>
                            <div className="flex items-center gap-2">
                              <p className="text-xl font-semibold text-green-500">
                                {monthlyStats.stats.gain_ventes ?? 0}€
                              </p>
                              {/* Flèche hausse/baisse */}
                              {(monthlyStats.stats.gain_ventes ?? 0) >
                              (monthlyStats.stats.gain_ventes_last_month ??
                                0) ? (
                                <span className="flex items-center text-green-500">
                                  <svg
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M10 3l6 6H4l6-6zm0 2.828L5.414 10h9.172L10 5.828z" />
                                  </svg>
                                  <span className="ml-1 text-xs">
                                    En hausse
                                  </span>
                                </span>
                              ) : (
                                <span className="flex items-center text-red-500">
                                  <svg
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M10 17l-6-6h12l-6 6zm0-2.828L14.586 10H5.414L10 14.172z" />
                                  </svg>
                                  <span className="ml-1 text-xs">
                                    En baisse
                                  </span>
                                </span>
                              )}
                            </div>
                            <p
                              className={`text-xs mt-2 ${isDark ? "text-green-400" : "text-green-600"}`}
                            >
                              Montant total gagné avec tes ventes personnelles
                              (avant déduction TVA, commission, Stripe).
                            </p>
                          </div>
                          {/* Somme totale des deux */}
                          <div
                            className={`p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                          >
                            <p
                              className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                            >
                              Total gains + commissions
                            </p>
                            <p className="text-2xl font-bold text-rose-500">
                              {(
                                (monthlyStats.stats.gain_ventes ?? 0) +
                                (monthlyStats.stats.platform_commission ?? 0)
                              ).toFixed(2)}
                              €
                            </p>
                            <p
                              className={`text-xs mt-2 ${isDark ? "text-rose-400" : "text-rose-600"}`}
                            >
                              Somme totale de tes gains personnels et des
                              commissions générées.
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div
                            className={`p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                          >
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <ShoppingBag className="w-5 h-5 text-rose-500" />
                              Commandes recentes
                            </h2>
                            <div className="space-y-3">
                              {recentOrders.length === 0 ? (
                                <p
                                  className={
                                    isDark ? "text-white/60" : "text-gray-600"
                                  }
                                >
                                  Aucune commande recente
                                </p>
                              ) : (
                                recentOrders.slice(0, 5).map((order, idx) => (
                                  <div
                                    key={idx}
                                    className={`p-3 rounded-lg border ${isDark ? "border-white/5 hover:bg-white/5" : "border-gray-100 hover:bg-gray-50"} transition-colors`}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-sm">
                                        {order.reference || `#${order.id}`}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded text-xs ${statusColors[order.status] || (isDark ? "bg-white/10 text-white" : "bg-gray-200 text-gray-700")}`}
                                      >
                                        {order.status}
                                      </span>
                                    </div>
                                    <p
                                      className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                                    >
                                      {order.user_name || "Client"} •{" "}
                                      {order.product_name || "Produit"}
                                    </p>
                                    <p className="text-sm font-semibold mt-1">
                                      {order.total || order.amount}€
                                    </p>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>

                          <div
                            className={`p-6 rounded-2xl border ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
                          >
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <Users className="w-5 h-5 text-blue-500" />
                              Nouveaux utilisateurs
                            </h2>
                            <div className="space-y-3">
                              {recentUsers.length === 0 ? (
                                <p
                                  className={
                                    isDark ? "text-white/60" : "text-gray-600"
                                  }
                                >
                                  Aucun nouvel utilisateur
                                </p>
                              ) : (
                                recentUsers.slice(0, 5).map((u, idx) => (
                                  <div
                                    key={idx}
                                    className={`p-3 rounded-lg border ${isDark ? "border-white/5 hover:bg-white/5" : "border-gray-100 hover:bg-gray-50"} transition-colors`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-sm">
                                        {u.firstname} {u.lastname}
                                      </span>
                                      <span
                                        className={`text-xs ${isDark ? "text-white/50" : "text-gray-500"}`}
                                      >
                                        {new Date(
                                          u.created_at,
                                        ).toLocaleDateString("fr-FR")}
                                      </span>
                                    </div>
                                    <p
                                      className={`text-sm ${isDark ? "text-white/60" : "text-gray-600"}`}
                                    >
                                      {u.email}
                                    </p>
                                    <span
                                      className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${isDark ? "bg-white/10" : "bg-gray-100"}`}
                                    >
                                      {u.role_name || "Utilisateur"}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {((user?.id_role === 3 && adminProfileTab === "account") ||
                  user?.id_role !== 3) && (
                  <div className="space-y-6">
                    {!user?.email_verified_at && (
                      <div
                        className={`p-3 rounded-lg ${isDark ? "bg-amber-500/10 border border-amber-500/30 text-amber-200" : "bg-amber-50 border border-amber-200 text-amber-700"}`}
                      >
                        Votre email n’est pas vérifié.{" "}
                        {emailNotice ||
                          "Un email de vérification vous sera envoyé si vous changez votre adresse."}
                      </div>
                    )}
                    {user?.email_verified_at && emailNotice && (
                      <div
                        className={`p-3 rounded-lg ${isDark ? "bg-blue-500/10 border border-blue-500/30 text-blue-200" : "bg-blue-50 border border-blue-200 text-blue-700"}`}
                      >
                        {emailNotice}
                      </div>
                    )}
                    <div>
                      <label className="block font-medium mb-2">Email</label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={handleProfileChange("email")}
                        className={`w-full px-4 py-2 rounded border ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-medium mb-2">Prénom</label>
                        <input
                          type="text"
                          value={profileForm.firstname}
                          onChange={handleProfileChange("firstname")}
                          className={`w-full px-4 py-2 rounded border ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                          placeholder="Votre prénom"
                        />
                      </div>
                      <div>
                        <label className="block font-medium mb-2">Nom</label>
                        <input
                          type="text"
                          value={profileForm.lastname}
                          onChange={handleProfileChange("lastname")}
                          className={`w-full px-4 py-2 rounded border ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                          placeholder="Votre nom"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-medium mb-2">Pseudo</label>
                      <input
                        type="text"
                        value={profileForm.nickname}
                        onChange={handleProfileChange("nickname")}
                        className={`w-full px-4 py-2 rounded border ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                        placeholder="Votre pseudo"
                      />
                    </div>
                    <button
                      onClick={handleSaveProfile}
                      disabled={profileSaving}
                      className={`px-6 py-2 rounded font-medium ${isDark ? "bg-rose-500 hover:bg-rose-600" : "bg-rose-600 hover:bg-rose-700"} text-white ${profileSaving ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {profileSaving
                        ? "Enregistrement..."
                        : "Enregistrer les modifications"}
                    </button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Modal Projet (créateur) */}
        <ModalAdmin
          isOpen={projectModalOpen}
          onClose={() => setProjectModalOpen(false)}
          title={
            projectFormMode === "create"
              ? "Créer un projet"
              : "Modifier le projet"
          }
          size="lg"
        >
          <form onSubmit={handleSaveProject} className="space-y-4">
            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Client <span className="text-rose-500">*</span>
              </label>
              <select
                value={projectFormData.id_user}
                onChange={(e) =>
                  setProjectFormData({
                    ...projectFormData,
                    id_user: e.target.value,
                  })
                }
                className={`w-full px-3 py-2 rounded-lg border ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-white border-gray-200"}`}
                required
              >
                <option value="">-- Sélectionner un client --</option>
                {usersList.map((u) => {
                  const id = u.id_user || u.id;
                  const label =
                    u.nickname ||
                    `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() ||
                    u.email;
                  return (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Nom du projet <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={projectFormData.name}
                  onChange={(e) =>
                    setProjectFormData({
                      ...projectFormData,
                      name: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-white border-gray-200"}`}
                  required
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Type
                </label>
                <input
                  type="text"
                  value={projectFormData.type}
                  onChange={(e) =>
                    setProjectFormData({
                      ...projectFormData,
                      type: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-white border-gray-200"}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Statut
                </label>
                <select
                  value={projectFormData.status}
                  onChange={(e) =>
                    setProjectFormData({
                      ...projectFormData,
                      status: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-white border-gray-200"}`}
                >
                  <option value="En cours">En cours</option>
                  <option value="En attente">En attente</option>
                  <option value="Terminé">Terminé</option>
                  <option value="Annulé">Annulé</option>
                </select>
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Progression: {projectFormData.progress}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={projectFormData.progress}
                  onChange={(e) =>
                    setProjectFormData({
                      ...projectFormData,
                      progress: parseInt(e.target.value, 10),
                    })
                  }
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Description
              </label>
              <textarea
                rows="3"
                value={projectFormData.description}
                onChange={(e) =>
                  setProjectFormData({
                    ...projectFormData,
                    description: e.target.value,
                  })
                }
                className={`w-full px-3 py-2 rounded-lg border ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-white border-gray-200"}`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Date de debut
                </label>
                <input
                  type="date"
                  value={projectFormData.start_date}
                  onChange={(e) =>
                    setProjectFormData({
                      ...projectFormData,
                      start_date: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-white border-gray-200"}`}
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Date de fin
                </label>
                <input
                  type="date"
                  value={projectFormData.end_date}
                  onChange={(e) =>
                    setProjectFormData({
                      ...projectFormData,
                      end_date: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-white border-gray-200"}`}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <button
                type="button"
                onClick={() => setProjectModalOpen(false)}
                className={`px-4 py-2 rounded-lg border ${isDark ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"}`}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
              >
                {projectFormMode === "create" ? "Creer" : "Modifier"}
              </button>
            </div>
          </form>
        </ModalAdmin>
      </div>
    </div>
  );
};

export default ClientSpace;
